import React from "react";
import { spacing } from "../../../design-system/tokens/spacing";
import { ModalHeader, ModalBody, ModalFooter } from "../../../design-system/components/composite/Modal/Modal";
import { DELIVER_COPY } from "./deliverStory.copy";
import { SoonBadge } from "../../atoms/SoonBadge";
import SendNowForm, { type SendNowFormProps } from "./SendNowForm";
import DeliveryPreview from "./DeliveryPreview";
import type {
  DeliveryAttachmentPreview,
  AlertConditionConfig,
  AlertConditionMatchMode,
  ScheduleConfig,
  SendNowFormState,
  Weekday,
} from "./types";
import type { ScheduleErrors } from "../../../utils/scheduleValidation";
import type { OpenDropdownKey } from "../../../hooks/useDeliverStory";
import type { DropdownOption } from "../../molecules/Dropdown";
import {
  ComposerModal,
  Title,
  Body,
  PreviewColumn,
  CancelLink,
  FooterRow,
  FooterLeft,
  FooterRight,
  TestDeliveryButton,
  PrimaryPillButton,
  FooterNote,
  LinkButton,
  FooterStack,
  SendErrorText,
} from "./SendNowModal.styles";

type ThemeFieldProps = Pick<
  SendNowFormProps,
  "themeOptions" | "themeValue" | "isThemesLoading" | "themesErrorMessage" | "showAudience"
>;

export interface SendNowModalProps extends ThemeFieldProps {
  /** The real rendered deck for the preview card (useDeliveryAttachmentPreview). */
  attachment?: DeliveryAttachmentPreview;
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  /** Opens the active-deliveries list. */
  onShowDeliveries?: () => void;
  /** Footer line such as the next scheduled send; omitted when none. */
  nextSendText?: string | null;
  onSend: () => void;
  onTestDelivery: () => void;
  form: SendNowFormState;
  openDropdown: OpenDropdownKey;
  scheduleErrors: ScheduleErrors;
  scheduleTouched: boolean;
  maxAlertConditions: number;
  canSubmit: boolean;
  /** Real story_canvas_items KPI options for the metric picker — see
   * useDeliverStory's alertMetricsQuery. */
  metricOptions: DropdownOption<string>[];
  currentMetricTextByMetric: Record<string, string>;
  /** Set when the last Slack send partially or fully failed — rendered
   * inline above the footer so it's visible before the user retries. */
  lastSendError?: string | null;
  /** True while a send is in flight — disables the submit button. */
  isSending?: boolean;
  onFormChange: (patch: Partial<SendNowFormState>) => void;
  onAddDestination: (dest: string) => void;
  onRemoveDestination: (dest: string) => void;
  onReplaceDestinations: (dests: string[]) => void;
  onScheduleChange: (patch: Partial<ScheduleConfig>) => void;
  onToggleWeekday: (day: Weekday) => void;
  onAddCondition: () => void;
  onUpdateCondition: (
    id: string,
    patch: Partial<Omit<AlertConditionConfig, "id">>,
  ) => void;
  onRemoveCondition: (id: string) => void;
  onConditionMatchModeChange: (mode: AlertConditionMatchMode) => void;
  onOpenDropdown: (key: Exclude<OpenDropdownKey, null>) => void;
  onCloseDropdown: () => void;
}

const SUBMIT_LABEL: Record<SendNowFormState["whenToSend"], string> = {
  "send-now": DELIVER_COPY.submitSendNow,
  schedule: DELIVER_COPY.submitSchedule,
  "alert-on-condition": DELIVER_COPY.submitAlert,
};

const SendNowModal: React.FC<SendNowModalProps> = ({
  open,
  onClose,
  onCancel,
  onShowDeliveries,
  nextSendText,
  onSend,
  onTestDelivery,
  form,
  openDropdown,
  scheduleErrors,
  scheduleTouched,
  maxAlertConditions,
  canSubmit,
  metricOptions,
  currentMetricTextByMetric,
  lastSendError,
  isSending,
  onFormChange,
  onAddDestination,
  onRemoveDestination,
  onReplaceDestinations,
  onScheduleChange,
  onToggleWeekday,
  onAddCondition,
  onUpdateCondition,
  onRemoveCondition,
  onConditionMatchModeChange,
  onOpenDropdown,
  onCloseDropdown,
  themeOptions,
  themeValue,
  isThemesLoading,
  themesErrorMessage,
  showAudience,
  attachment,
}) => {
  return (
    <ComposerModal open={open} onClose={onClose} size="composer">
      <ModalHeader
        onClose={onClose}
        padding={`0 ${spacing[4]} 0 ${spacing[6]}`}
        minHeight={spacing[16]}
      >
        <Title>{DELIVER_COPY.title}</Title>
      </ModalHeader>

      <ModalBody overflowY="hidden" padding="0">
        <Body>
          <SendNowForm
            value={form}
            openDropdown={openDropdown}
            scheduleErrors={scheduleErrors}
            scheduleTouched={scheduleTouched}
            maxAlertConditions={maxAlertConditions}
            metricOptions={metricOptions}
            currentMetricTextByMetric={currentMetricTextByMetric}
            onChange={onFormChange}
            onAddDestination={onAddDestination}
            onRemoveDestination={onRemoveDestination}
            onReplaceDestinations={onReplaceDestinations}
            onScheduleChange={onScheduleChange}
            onToggleWeekday={onToggleWeekday}
            onAddCondition={onAddCondition}
            onUpdateCondition={onUpdateCondition}
            onRemoveCondition={onRemoveCondition}
            onConditionMatchModeChange={onConditionMatchModeChange}
            onOpenDropdown={onOpenDropdown}
            onCloseDropdown={onCloseDropdown}
            themeOptions={themeOptions}
            themeValue={themeValue}
            isThemesLoading={isThemesLoading}
            themesErrorMessage={themesErrorMessage}
            showAudience={showAudience}
          />
          <PreviewColumn>
            <DeliveryPreview
              platform={form.platform}
              message={form.message}
              format={form.format}
              attachment={attachment}
            />
          </PreviewColumn>
        </Body>
      </ModalBody>

      <ModalFooter padding={`0 ${spacing[6]}`}>
        <FooterStack>
          {lastSendError && <SendErrorText role="alert">{lastSendError}</SendErrorText>}
          <FooterRow>
            <FooterLeft>
              {nextSendText ? <FooterNote>{nextSendText}</FooterNote> : null}
              {onShowDeliveries && (
                <LinkButton type="button" onClick={onShowDeliveries}>
                  {DELIVER_COPY.activeDeliveries}
                </LinkButton>
              )}
            </FooterLeft>
            <FooterRight>
              {/* Email/Teams send a real test; Slack has none yet, so it
                  shows the peach Coming soon state instead of vanishing. */}
              {form.platform !== "slack" ? (
                <TestDeliveryButton variant="secondary" onClick={onTestDelivery}>
                  {DELIVER_COPY.sendTest}
                </TestDeliveryButton>
              ) : (
                /* GAP: no backend sends a Slack test message yet. */
                <TestDeliveryButton variant="secondary" disabled>
                  {DELIVER_COPY.sendTest}
                  <SoonBadge />
                </TestDeliveryButton>
              )}
              <CancelLink type="button" onClick={onCancel}>
                {DELIVER_COPY.cancel}
              </CancelLink>
              <PrimaryPillButton
                variant="primary"
                disabled={!canSubmit || isSending}
                onClick={onSend}
              >
                {isSending ? DELIVER_COPY.sending : SUBMIT_LABEL[form.whenToSend]}
              </PrimaryPillButton>
            </FooterRight>
          </FooterRow>
        </FooterStack>
      </ModalFooter>
    </ComposerModal>
  );
};

export default SendNowModal;
